import { Link } from 'react-router';
import { LAGRINGSTID_MAANEDER } from '../lib/lagringstid';
import { SELSKAP } from '../lib/selskap';
import { SIDETITLER, useSidehode } from '../lib/sidehode';
import { SVARLENKE_DAGER } from '../lib/svarlenke';

/**
 * Det vi selv skylder gjestene å fortelle.
 *
 * Vi er behandlingsansvarlige for opplysningene om gjestene – artikkel 13 for
 * det gjesten selv skriver i svarskjemaet, artikkel 14 for det brudeparet la
 * inn om dem. Da er informasjonen vår egen plikt, ikke en tekst vi tilbyr
 * brudeparet å vise til, og siden er skrevet deretter.
 *
 * Svarsiden bærer de fire linjene som må leses før noe sendes. Her står resten,
 * for den som trykker på lenken: hva som registreres, hva det hviler på, hvor
 * lenge det ligger, og hvordan man får det rettet eller slettet.
 *
 * Teksten er med vilje generisk: den nevner ikke hvilket bryllup gjesten er
 * invitert til. Adressen er åpen, og hvem som gifter seg med hvem, er ikke noe
 * vi legger ut.
 */
export function GjesteinformasjonPage() {
  useSidehode(SIDETITLER.gjesteinformasjon, '/gjesteinformasjon');
  return (
    <div className="legal-page">
      <Link to="/" className="small">
        ← Til forsiden
      </Link>
      <h1>Til deg som har fått en invitasjon</h1>
      <p className="muted">
        Du har fått en lenke der du kan svare på om du kommer i et bryllup. Her står det hva
        som skjer med det du skriver.
      </p>

      <h2>Hvem som har ansvaret</h2>
      <p>
        <strong>vardag.no AS</strong> lager og driver tjenesten brudeparet planlegger i, og det
        er vi som er <em>behandlingsansvarlige</em> for opplysningene om deg: vi har bestemt
        hvilke felt som finnes, hvordan svarlenken virker, hvordan opplysningene sikres og når
        de slettes.
      </p>
      <p>
        <strong>Brudeparet som inviterte deg</strong> bestemmer hvem som skal stå på gjestelista
        og hva som skal noteres om dem. De har lovet oss å legge inn bare det som trengs for å
        planlegge dagen, og å ha spurt deg først om det de skriver om mat.
      </p>

      <h2>Hva som registreres, og hvorfor</h2>
      <p>
        Formålet er å holde styr på invitasjonen og svaret ditt, og å planlegge dagen – hvem som
        kommer, hvor mange det skal dekkes til, hvem som sitter hvor, og hva som skal serveres.
      </p>
      <ul>
        <li>
          <strong>Navnet ditt</strong>, og det brudeparet ellers har lagt inn for å nå deg –
          som regel e-post eller telefonnummer.
        </li>
        <li>
          <strong>Svaret ditt</strong>: om du kommer, og eventuelt hvem du tar med.
        </li>
        <li>
          <strong>Det du skriver om mat.</strong> Skriver du at du har nøtteallergi eller er
          glutenintolerant, sier det noe om helsa di. Skriv bare det som trengs for at du skal
          få mat du tåler – resten trenger verken vi eller brudeparet å vite.
        </li>
      </ul>

      <h2>Hva det hviler på</h2>
      <p>
        For navn, kontaktopplysninger og svaret ditt er grunnlaget <strong>berettiget
        interesse</strong>: et par som skal holde et selskap, må kunne holde styr på hvem som
        kommer, og har du fått en invitasjon, venter du at svaret ditt blir notert et sted.
      </p>
      <p>
        For <strong>allergier og kosthold</strong> er grunnlaget ditt eget{' '}
        <strong>samtykke</strong>. Skriver du dem inn selv, krysser du av for det først. Legger
        brudeparet dem inn på dine vegne, må de bekrefte at de har spurt deg og fått ja. Vil du
        ikke oppgi noe, lar du feltet stå tomt – svaret ditt om at du kommer, tas imot likevel.
      </p>

      <h2>Hvem som ser det</h2>
      <p>
        Brudeparet, og de de har gitt tilgang til gjestelista – det kan være en partner, en
        toastmaster eller en forlover. Ingen andre gjester ser hva du har svart. Hos oss er
        e-post, telefon, allergier og notater kryptert, og de som drifter tjenesten ser antall
        gjester, ikke navnene.
      </p>

      <h2>Hvor lenge det blir liggende</h2>
      <p>
        Vi sletter opplysningene om gjestene <strong>{LAGRINGSTID_MAANEDER} måneder etter
        bryllupsdagen</strong>, av oss selv. Brudeparet trenger ikke gjøre noe, og du trenger
        ikke be om det.
      </p>

      <h2>Rettighetene dine</h2>
      <p>
        Du kan spørre hva som er registrert om deg, få det rettet, be om å bli slettet, og
        protestere mot at vi behandler opplysningene i det hele tatt. Siden det er vi som er
        ansvarlige, er det <strong>oss du spør</strong> – ikke brudeparet.
      </p>
      <p>
        <strong>Det gjør du fra svarlenken din.</strong> Nederst på svarsida står «Hva har dere om
        meg?». Der kan du be om en kopi av alt vi har, eller om å bli slettet. Kopien sendes til
        e-postadressen som står på deg, ikke til nettleseren – lenken kan ha blitt videresendt, og
        da skal ikke den som har den, få lese det som står om deg.
      </p>
      <p>
        Ber du om å bli slettet, sier vi fra til brudeparet om at du er ute av lista, men ikke
        hvorfor. Adressen din kan da ikke legges inn i det bryllupet igjen.
      </p>
      <p>
        Har du ingen svarlenke, eller står det ingen e-postadresse på deg, når du oss på{' '}
        <a href={`mailto:${SELSKAP.epost}`}>{SELSKAP.epost}</a>. Det raskeste for en skrivefeil er
        ofte å si det til brudeparet – de retter den selv, med én gang. Du kan også klage til
        Datatilsynet.
      </p>

      <h2>Svarlenken din</h2>
      <p>
        Lenken du fikk, er personlig. Den som har den, kan se navnet på brudeparet, dato og
        sted, og svare på dine vegne. Andre gjester er ikke synlige. Del den derfor ikke videre.
      </p>
      <p>
        <strong>Den slutter å virke {SVARLENKE_DAGER} dager etter bryllupsdagen.</strong> Har du
        delt den med noen, eller sendt den videre ved et uhell, kan brudeparet lage en ny til deg
        — da dør den gamle med én gang.
      </p>
      <p>
        Det du har skrevet om mat, <strong>viser vi ikke igjen</strong> på lenken. Du får vite at
        noe er registrert, og kan skrive det på nytt eller be om å få det fjernet – men den som
        skulle få lenken din i hendene, får ikke lese det.
      </p>

      <h2>Resten</h2>
      <p>
        Hvordan tjenesten ellers behandler opplysninger – drift, sikkerhet, leverandører og
        rettighetene til dem som har konto – står i{' '}
        <Link to="/personvern">personvernerklæringen vår</Link>.
      </p>
    </div>
  );
}
